// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: short times for action chips: "today 16:00", "tomorrow 09:00",
// "Fri 09:00", "Thu 14:00–14:30", "12 Oct". Built from Intl so they follow
// the locale without catalog strings; pure so they are tested.

function startOfDay(at: Date): number {
	return new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
}

function dayDiff(at: Date, now: Date): number {
	return Math.round((startOfDay(at) - startOfDay(now)) / 86_400_000);
}

function clock(at: Date, locale: string): string {
	return new Intl.DateTimeFormat(locale, {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).format(at);
}

/** "today", "tomorrow", "Fri" (within the week) or "12 Oct". */
export function chipDay(at: Date, now: Date, locale: string): string {
	const diff = dayDiff(at, now);
	if (diff === 0 || diff === 1) {
		return new Intl.RelativeTimeFormat(locale, {numeric: 'auto'}).format(diff, 'day');
	}
	if (diff > 1 && diff < 7) return new Intl.DateTimeFormat(locale, {weekday: 'short'}).format(at);
	return new Intl.DateTimeFormat(locale, {day: 'numeric', month: 'short'}).format(at);
}

/** A moment or a range: "tomorrow 09:00", "Thu 14:00–14:30". */
export function chipWhen(start: string, end: string | null, locale: string, now: Date = new Date()): string {
	const from = new Date(start);
	if (Number.isNaN(from.getTime())) return '';
	const day = chipDay(from, now, locale);
	const to = end ? new Date(end) : null;
	if (to && !Number.isNaN(to.getTime()) && dayDiff(to, from) === 0) {
		return `${day} ${clock(from, locale)}–${clock(to, locale)}`;
	}
	return `${day} ${clock(from, locale)}`;
}

/** A due date: the day, plus the time when one was stated (not midnight or end of day). */
export function chipDue(due: string, locale: string, now: Date = new Date()): string {
	const at = new Date(due);
	if (Number.isNaN(at.getTime())) return '';
	const day = chipDay(at, now, locale);
	const minutes = at.getHours() * 60 + at.getMinutes();
	const timed = minutes !== 0 && minutes !== 23 * 60 + 59 && minutes !== 17 * 60;
	return timed ? `${day} ${clock(at, locale)}` : day;
}

export function firstName(name: string | null | undefined): string {
	return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

/** "Gary", "Gary and Engen", "Gary, Engen and Ben". */
export function nameList(names: ReadonlyArray<string>, locale: string): string {
	const firsts = names.map(firstName).filter(Boolean);
	try {
		return new Intl.ListFormat(locale, {type: 'conjunction', style: 'long'}).format(firsts);
	} catch {
		return firsts.join(', ');
	}
}
